import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Calendar,
  Clock,
  Users,
  Truck,
  FileText,
  CheckCircle2,
  Circle,
  XCircle,
  AlertCircle,
  AlertTriangle,
  Pause,
  Play,
  ChevronRight,
  Plus,
  Trash2,
  Loader2,
  History,
  FolderOpen,
  CalendarClock,
  ClipboardCheck,
  Unlock,
  Lock,
  DollarSign,
  Receipt,
  Archive,
  MapPin,
  Mail,
  Phone,
  ChevronRight as ChevronRightIcon,
  Flag,
} from 'lucide-react';
import StatusChangeModal from '@/components/StatusChangeModal';
import RescheduleModal from '@/components/RescheduleModal';
import RailcarRepairStatusNotificationForm from '@/components/RailcarRepairStatusNotificationForm';
import RailcarRepairStatusNotificationReviewModal from '@/components/RailcarRepairStatusNotificationReviewModal';
import {
  WS_STATUS_CONFIG,
  getValidWSTransitions,
  canSetCancelled,
  canSetCancelledForRole,
  canSetReschedule,
  canUseRescheduleButton
} from '@/config/statusWorkflow';

// Icon mapping for status display
const STATUS_ICONS = {
  SCHEDULED: Calendar,
  IN_PROCESS: Play,
  ON_HOLD: Pause,
  COMPLETE: CheckCircle2,
  INCOMPLETE: AlertTriangle,
  QA_REVIEW: ClipboardCheck,
  RELEASE: Unlock,
  BILLING: DollarSign,
  INVOICED: Receipt,
  CLOSED: Archive,
  RESCHEDULE: CalendarClock,
  CANCELLED: XCircle
};

/**
 * Check if a work schedule is overdue.
 * A schedule is overdue if status is "SCHEDULED" and date is in the past.
 */
const isScheduleOverdue = (schedule) => {
  if (!schedule || schedule.status !== 'SCHEDULED') return false;
  
  const dateStr = schedule.schedule_end_date || schedule.schedule_start_date || schedule.schedule_date;
  if (!dateStr) return false;
  
  const [year, month, day] = dateStr.split('-').map(Number);
  if (!year || !month || !day) return false;
  
  const scheduleDate = new Date(year, month - 1, day, 23, 59, 59);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  return scheduleDate < today;
};

const WorkScheduleDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, api, hasRole } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [schedule, setSchedule] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  
  // Modal states
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [rescheduleModalOpen, setRescheduleModalOpen] = useState(false);
  // 2026-06-05 — Repair Site read-only modal trigger.
  const [siteModalOpen, setSiteModalOpen] = useState(false);
  
  const [addLineItemOpen, setAddLineItemOpen] = useState(false);
  const [newLineItemDesc, setNewLineItemDesc] = useState('');
  const [addingLineItem, setAddingLineItem] = useState(false);
  
  const [completeItemId, setCompleteItemId] = useState(null);
  const [completeNotes, setCompleteNotes] = useState('');
  const [completingItem, setCompletingItem] = useState(false);
  
  const [incompleteItemId, setIncompleteItemId] = useState(null);
  const [incompleteReason, setIncompleteReason] = useState('');
  const [markingIncomplete, setMarkingIncomplete] = useState(false);
  
  const [deleteItemId, setDeleteItemId] = useState(null);
  
  const [historyOpen, setHistoryOpen] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);

  // Railcar Repair Status Notification (v1) — snapshot form + review state.
  const [rrsnFormOpen, setRrsnFormOpen] = useState(false);
  const [rrsnOutcome, setRrsnOutcome] = useState(null);   // 'COMPLETE' | 'INCOMPLETE'
  const [rrsnIssueReason, setRrsnIssueReason] = useState('');
  const [rrsnList, setRrsnList] = useState([]);
  const [rrsnReviewOpen, setRrsnReviewOpen] = useState(false);
  const [rrsnActive, setRrsnActive] = useState(null);

  const fetchAuditHistory = useCallback(async () => {
    try {
      const response = await api.get(`/work-schedules/${id}/audit-history`);
      setAuditLogs(response.data.audit_logs || []);
    } catch (error) {
      console.error('Failed to fetch audit history:', error);
    }
  }, [api, id]);

  const loadSchedule = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/work-schedules/${id}`);
      setSchedule(response.data);
      setLineItems(response.data.line_items || []);
    } catch (error) {
      console.error('Failed to load work schedule:', error);
      toast.error('Failed to load work schedule');
    } finally {
      setLoading(false);
    }
  }, [api, id]);

  useEffect(() => {
    if (id) {
      loadSchedule();
      fetchAuditHistory();
    }
  }, [id, loadSchedule, fetchAuditHistory]);

  // Get status config with fallback
  const getStatusConfig = (status) => {
    return WS_STATUS_CONFIG[status] || { label: status, color: 'bg-gray-100 text-gray-700' };
  };

  // Calculate line items completion
  const totalLineItems = lineItems.length;
  const completedLineItems = lineItems.filter(item => item.status === 'COMPLETE').length;
  const lineItemsComplete = totalLineItems > 0 && completedLineItems === totalLineItems;

  // ========================================
  // PERMISSIONS — sourced from backend _permissions object
  // ========================================
  const perms = schedule?._permissions || {};
  const isOperatorPlus = hasRole(['ADMIN', 'MANAGER', 'OPERATOR']);
  
  // Status change: only job lead or Operator+
  const hasAvailableTransitions = () => {
    if (!schedule) return false;
    if (schedule.status === 'CANCELLED' || schedule.status === 'CLOSED') return false;
    if (!perms.can_change_status) return false;
    
    const userRole = user?.role || 'OPERATOR';
    if (schedule.status === 'INCOMPLETE' && userRole === 'TECHNICIAN') return false;
    
    const normalTransitions = getValidWSTransitions(schedule.status, userRole, lineItemsComplete).length > 0;
    const canCancelWS = canSetCancelledForRole(schedule.status, 'ws', userRole);
    const canRequestReschedule = canSetReschedule(schedule.status);
    return normalTransitions || canCancelWS || canRequestReschedule;
  };

  // RULE 3: Reschedule button only shown when:
  // - User is Operator+ (not Technician)
  // - Status is RESCHEDULE
  const showRescheduleButton = () => {
    if (!schedule) return false;
    const userRole = user?.role || 'OPERATOR';
    // Only show when status is RESCHEDULE and user is Operator+
    if (schedule.status !== 'RESCHEDULE') return false;
    if (userRole === 'TECHNICIAN') return false;
    return true;
  };

  const handleStatusChanged = () => {
    loadSchedule();
    loadRrsnList();
  };

  const loadRrsnList = useCallback(async () => {
    if (!id) return;
    try {
      const { data } = await api.get(`/railcar-status-notifications/work-schedule/${id}`);
      setRrsnList(data || []);
    } catch { /* non-blocking */ }
  }, [api, id]);

  useEffect(() => { loadRrsnList(); }, [loadRrsnList]);

  const downloadRrsnPdf = async (snapshot) => {
    try {
      const response = await api.get(
        `/railcar-status-notifications/${snapshot.id}/pdf`,
        { responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `railcar-status-${snapshot.sr_number || snapshot.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'PDF download failed.');
    }
  };

  const handleRescheduleComplete = () => {
    loadSchedule();
  };

  const handleAddLineItem = async () => {
    if (!newLineItemDesc.trim()) {
      toast.error('Please enter a description');
      return;
    }
    
    setAddingLineItem(true);
    try {
      await api.post(`/work-schedules/${id}/line-items`, {
        description: newLineItemDesc.trim(),
        display_order: lineItems.length
      });
      
      toast.success('Line item added');
      setAddLineItemOpen(false);
      setNewLineItemDesc('');
      loadSchedule();
    } catch (error) {
      toast.error('Failed to add line item');
    } finally {
      setAddingLineItem(false);
    }
  };

  const handleCompleteItem = async () => {
    if (!completeItemId) return;

    setCompletingItem(true);
    try {
      const resp = await api.put(`/work-schedules/line-items/${completeItemId}/complete`, null, {
        params: { notes: completeNotes || null }
      });

      toast.success('Line item marked as complete');
      setCompleteItemId(null);
      setCompleteNotes('');
      loadSchedule();

      // Pass A #3: if the backend signals all-items-complete + no issues,
      // open the required Complete notification form (mirrors the auto-
      // INCOMPLETE flow). Snapshot POST flips WS to COMPLETE.
      const autoChange = resp?.data?.auto_status_change;
      if (autoChange?.pending_complete_snapshot) {
        setRrsnOutcome('COMPLETE');
        setRrsnIssueReason('');
        setRrsnFormOpen(true);
      }
    } catch (error) {
      toast.error('Failed to complete line item');
    } finally {
      setCompletingItem(false);
    }
  };

  const handleIncompleteItem = async () => {
    if (!incompleteItemId || !incompleteReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }

    setMarkingIncomplete(true);
    try {
      const resp = await api.put(`/work-schedules/line-items/${incompleteItemId}/incomplete`, null, {
        params: { notes: incompleteReason.trim() }
      });

      toast.success('Line item marked as incomplete');
      const reasonCaptured = incompleteReason.trim();
      setIncompleteItemId(null);
      setIncompleteReason('');
      loadSchedule();

      // v1 Railcar Repair Status Notification — if the backend signals
      // that all items are now marked AND at least one is an Issue, open
      // the required snapshot form. The WS status flip to INCOMPLETE
      // happens inside the snapshot POST once the technician submits.
      const autoChange = resp?.data?.auto_status_change;
      if (autoChange?.pending_incomplete_snapshot) {
        setRrsnOutcome('INCOMPLETE');
        setRrsnIssueReason(reasonCaptured);
        setRrsnFormOpen(true);
      }
    } catch (error) {
      toast.error('Failed to mark item as incomplete');
    } finally {
      setMarkingIncomplete(false);
    }
  };

  const handleDeleteLineItem = async () => {
    if (!deleteItemId) return;
    
    try {
      await api.delete(`/work-schedules/line-items/${deleteItemId}`);
      toast.success('Line item deleted');
      setDeleteItemId(null);
      loadSchedule();
    } catch (error) {
      toast.error('Failed to delete line item');
    }
  };

  // RULE 1 & 2: Permission checks based on status and role
  // Statuses where line items can be added/edited (before Complete/Incomplete)
  const editableStatuses = ['SCHEDULED', 'IN_PROCESS', 'ON_HOLD', 'RESCHEDULE'];
  // Status where line item Complete/Issue actions are allowed
  const actionableStatus = 'IN_PROCESS';
  // Final statuses where everything is locked
  const finalStatuses = ['CANCELLED', 'CLOSED'];
  // LINE ITEM LOCK: Statuses where line items become fully read-only for ALL roles
  const lineItemLockedStatuses = ['COMPLETE', 'INCOMPLETE', 'QA_REVIEW', 'RELEASE', 'BILLING', 'INVOICED', 'CLOSED', 'CANCELLED'];
  // INCOMPLETE status - locked for technicians (view only), operator+ can override status
  const isIncomplete = schedule && schedule.status === 'INCOMPLETE';
  const isComplete = schedule && schedule.status === 'COMPLETE';
  // LINE ITEM LOCK FIX: Check if work order has EVER reached Complete/Incomplete
  // This flag is permanent - even if status goes to RESCHEDULE, line items stay locked
  const hasReachedCompletion = schedule && schedule.completion_reached === true;
  // Check if line items are locked (by current status OR by completion history)
  const areLineItemsLocked = schedule && (lineItemLockedStatuses.includes(schedule.status) || hasReachedCompletion);
  
  const isEditableStatus = schedule && editableStatuses.includes(schedule.status);
  const isActionableStatus = schedule && schedule.status === actionableStatus;
  const isFinalStatus = schedule && finalStatuses.includes(schedule.status);
  
  // LINE ITEM PERMISSIONS — driven by backend _permissions
  // Only Operator+ can add/delete line items (and only if status allows)
  const canManageLineItems = isOperatorPlus && isEditableStatus && !areLineItemsLocked;
  
  // Only job lead or Operator+ can mark Complete/Issue
  const canCompleteItems = perms.can_update_line_items && isActionableStatus && !areLineItemsLocked;
  
  // Technician view-only mode for INCOMPLETE status
  const isTechnicianViewOnly = hasRole(['TECHNICIAN']) && isIncomplete;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#005EF8]"></div>
      </div>
    );
  }

  if (!schedule) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-[#323033] mb-2">Work Schedule Not Found</h2>
        <Button onClick={() => navigate('/work-schedules')}>Back to List</Button>
      </div>
    );
  }

  const sr = schedule.service_request;
  const statusConfig = getStatusConfig(schedule.status);
  const StatusIcon = STATUS_ICONS[schedule.status] || Circle;
  const completedCount = lineItems.filter(li => li.status === 'COMPLETE').length;
  const totalCount = lineItems.length;
  const completionPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="work-schedule-detail-page">
      {/* Breadcrumbs */}
      <nav className="flex items-center gap-2 text-sm text-[#5A5A5A]">
        <Link to="/work-schedules" className="hover:text-[#005EF8] transition-colors">
          Work Schedules
        </Link>
        <ChevronRight className="w-4 h-4" />
        <span className="text-[#323033] font-medium">{schedule.job_number || sr?.job_number}</span>
      </nav>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/work-schedules')}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold text-[#323033]">
                {sr?.car_mark} {sr?.car_number}
              </h1>
              <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${statusConfig.color}`}>
                <StatusIcon className="w-4 h-4" />
                {statusConfig.label}
              </span>
              {isScheduleOverdue(schedule) && (
                <span className="px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700 border border-red-200">
                  Overdue
                </span>
              )}
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                schedule.priority === 'CRITICAL' ? 'bg-red-100 text-red-700' :
                schedule.priority === 'HIGH' ? 'bg-orange-100 text-orange-700' :
                schedule.priority === 'MEDIUM' ? 'bg-yellow-100 text-yellow-700' :
                'bg-green-100 text-green-700'
              }`}>
                {schedule.priority}
              </span>
            </div>
            <p className="text-[#5A5A5A] mt-1">
              {sr?.job_number} • {sr?.car_owner}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setHistoryOpen(true)}
            data-testid="view-history-btn"
          >
            <History className="w-4 h-4 mr-2" />
            History
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate(`/car-files/${sr?.car_mark}/${sr?.car_number}`)}
            data-testid="view-car-file-btn"
          >
            <FolderOpen className="w-4 h-4 mr-2" />
            Car File
          </Button>
          {showRescheduleButton() && (
            <Button
              variant="outline"
              onClick={() => {
                // FINAL PROMPT: Navigate to Add Work Schedule with minimal pre-population
                const params = new URLSearchParams({
                  rescheduleFrom: schedule.id,
                  rescheduleNote: schedule.reschedule_reason || 'Rescheduled'
                });
                navigate(`/work-schedules/add?${params.toString()}`);
              }}
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
              data-testid="create-new-schedule-btn"
            >
              <CalendarClock className="w-4 h-4 mr-2" />
              Create New Schedule
            </Button>
          )}
          {hasAvailableTransitions() && (
            <Button
              onClick={() => setStatusModalOpen(true)}
              className="bg-[#005EF8] hover:bg-[#0047C2] text-white"
              data-testid="change-status-btn"
            >
              Change Status
            </Button>
          )}
        </div>
      </div>

      {/* Reschedule Banner - FINAL PROMPT: Shows when status is RESCHEDULE */}
      {schedule.status === 'RESCHEDULE' && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium text-amber-800">Reschedule Requested</p>
            {schedule.reschedule_reason && (
              <p className="text-sm text-amber-700 mt-1">
                {schedule.reschedule_requested_by && (
                  <span className="font-medium">{schedule.reschedule_requested_by}: </span>
                )}
                {schedule.reschedule_reason}
              </p>
            )}
            {user?.role !== 'TECHNICIAN' && (
              <p className="text-sm text-amber-600 mt-2">
                Use the &quot;Create New Schedule&quot; button to update this work order with new scheduling details.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Cancelled Banner */}
      {schedule.status === 'CANCELLED' && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-red-800">This work order has been cancelled.</p>
            {schedule.cancellation_reason && (
              <p className="text-sm text-red-700 mt-1">
                {schedule.cancelled_by_name && (
                  <span className="font-medium">{schedule.cancelled_by_name}: </span>
                )}
                {schedule.cancellation_reason}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Closed Banner */}
      {schedule.status === 'CLOSED' && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
          <Archive className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-green-800">This work order is closed and archived.</p>
            {schedule.closed_at && (
              <p className="text-sm text-green-700 mt-1">
                Closed on {new Date(schedule.closed_at).toLocaleDateString()}
                {schedule.closed_by_name && ` by ${schedule.closed_by_name}`}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Incomplete Banner */}
      {schedule.status === 'INCOMPLETE' && (
        <div className="p-4 bg-amber-50 border border-amber-300 rounded-lg flex items-start gap-3" data-testid="incomplete-banner">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-800">
              This work order is incomplete - one or more line items have issues.
            </p>
            <p className="text-sm text-amber-700 mt-1">
              {isTechnicianViewOnly 
                ? 'An operator will review and determine next steps. Line items are locked.'
                : 'Use Change Status to override to Complete or choose Reschedule/Cancel.'
              }
            </p>
            {/* Show issue count */}
            {schedule.line_items && (
              <p className="text-xs text-amber-600 mt-2">
                {schedule.line_items.filter(li => li.status === 'INCOMPLETE').length} item(s) marked with Issue
              </p>
            )}
          </div>
        </div>
      )}

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isScheduleOverdue(schedule) ? 'bg-red-100' : 'bg-[#005EF8]/10'}`}>
                <Calendar className={`w-5 h-5 ${isScheduleOverdue(schedule) ? 'text-red-600' : 'text-[#005EF8]'}`} />
              </div>
              <div>
                <p className="text-sm text-[#5A5A5A]">Schedule Date</p>
                <p className={`font-medium ${isScheduleOverdue(schedule) ? 'text-red-600' : ''}`}>
                  {(() => {
                    const startDate = schedule.schedule_start_date || schedule.schedule_date;
                    const endDate = schedule.schedule_end_date;
                    if (!startDate) return '-';
                    const start = new Date(startDate + 'T00:00:00').toLocaleDateString();
                    if (!endDate || endDate === startDate) return start;
                    const end = new Date(endDate + 'T00:00:00').toLocaleDateString();
                    return `${start} – ${end}`;
                  })()}
                  {isScheduleOverdue(schedule) && <span className="ml-2 text-xs font-semibold">(Overdue)</span>}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#005EF8]/10 flex items-center justify-center">
                <Clock className="w-5 h-5 text-[#005EF8]" />
              </div>
              <div>
                <p className="text-sm text-[#5A5A5A]">Days Open</p>
                <p className="font-medium" style={{ color: schedule.days_open_color }}>
                  {schedule.days_open} days
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 2026-06-05 — Repair Site card. Replaces the Crew card (which now
            sits below the 4-card row at full width). Clickable; opens a
            read-only modal showing the linked Customers & Contacts repair
            site, including any "Entry Requirements" the operator captured. */}
        <Card
          role="button"
          tabIndex={0}
          onClick={() => setSiteModalOpen(true)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSiteModalOpen(true); } }}
          className="cursor-pointer transition-shadow hover:shadow-md hover:border-[#005EF8]/40"
          data-testid="ws-repair-site-card"
        >
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#005EF8]/10 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-[#005EF8]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[#5A5A5A]">Repair Site</p>
                <p className="font-medium truncate" data-testid="ws-repair-site-name">
                  {schedule.service_request?.repair_site
                    || schedule.service_request?.site_name
                    || '—'}
                </p>
                <p className="text-xs text-[#005EF8] mt-0.5 inline-flex items-center gap-0.5">
                  Entry Requirements
                  <ChevronRightIcon className="w-3 h-3" />
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#005EF8]/10 flex items-center justify-center">
                <Truck className="w-5 h-5 text-[#005EF8]" />
              </div>
              <div>
                <p className="text-sm text-[#5A5A5A]">Vehicles</p>
                <p className="font-medium">
                  {schedule.vehicle_not_needed ? 'Not needed' : 
                   schedule.vehicles?.length > 0 ? schedule.vehicles.map(v => v.display_id).join(', ') : 'None assigned'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Crew — full-width row directly below the 4 summary cards
          (moved 2026-06-05 to make room for the Repair Site card above). */}
      <Card data-testid="ws-crew-row">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-[#005EF8]/10 flex items-center justify-center shrink-0">
              <Users className="w-5 h-5 text-[#005EF8]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-[#5A5A5A]">Crew</p>
              <div className="font-medium flex flex-wrap items-center gap-x-2 gap-y-1">
                {schedule.job_lead ? (
                  <span data-testid="ws-crew-lead">
                    {schedule.job_lead.first_name} {schedule.job_lead.last_name}
                    <span className="text-xs text-[#005EF8] ml-1">(Lead)</span>
                  </span>
                ) : (
                  <span className="italic text-[#9A9A9A]">No lead assigned</span>
                )}
                {schedule.support_team && schedule.support_team.length > 0 && (
                  schedule.support_team.map((tech) => (
                    <span key={tech.id} data-testid={`ws-crew-member-${tech.id}`}>
                      , {tech.first_name} {tech.last_name}
                    </span>
                  ))
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Line Items */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-[#005EF8]" />
                Line Items
              </CardTitle>
              <CardDescription>
                {completedCount} of {totalCount} items complete ({completionPercent}%)
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {/* LINE ITEM LOCK MESSAGE: Show when items are locked after Complete/Incomplete */}
              {areLineItemsLocked && hasRole(['ADMIN', 'MANAGER', 'OPERATOR']) && (
                <span className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded flex items-center gap-1">
                  <Lock className="w-3 h-3" />
                  {hasReachedCompletion && schedule.status === 'RESCHEDULE'
                    ? 'Line items permanently locked (previously completed)'
                    : `Line items locked after ${schedule.status.replace('_', ' ').toLowerCase()}`
                  }
                </span>
              )}
              {canManageLineItems && (
                <Button
                  variant="outline"
                  onClick={() => setAddLineItemOpen(true)}
                  data-testid="add-line-item-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Item
                </Button>
              )}
            </div>
          </div>
          {/* Progress bar */}
          <div className="w-full bg-gray-200 rounded-full h-2 mt-4">
            <div
              className="bg-[#005EF8] h-2 rounded-full transition-all duration-500"
              style={{ width: `${completionPercent}%` }}
            />
          </div>
        </CardHeader>
        <CardContent>
          {lineItems.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle2 className="w-12 h-12 text-[#D3D5D4] mx-auto mb-4" />
              <p className="text-[#5A5A5A]">No line items yet</p>
              {canManageLineItems && (
                <Button
                  variant="outline"
                  onClick={() => setAddLineItemOpen(true)}
                  className="mt-4"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Line Item
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {lineItems.map((item, index) => (
                <div
                  key={item.id}
                  className={`p-4 rounded-lg border transition-colors ${
                    item.status === 'COMPLETE' ? 'bg-green-50 border-green-200' :
                    item.status === 'INCOMPLETE' ? 'bg-red-50 border-red-200' :
                    'bg-white border-border hover:border-[#005EF8]/30'
                  }`}
                  data-testid={`line-item-${item.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        item.status === 'COMPLETE' ? 'bg-green-100 text-green-700' :
                        item.status === 'INCOMPLETE' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {item.status === 'COMPLETE' ? (
                          <CheckCircle2 className="w-5 h-5" />
                        ) : item.status === 'INCOMPLETE' ? (
                          <XCircle className="w-5 h-5" />
                        ) : (
                          <span className="text-sm font-medium">{index + 1}</span>
                        )}
                      </div>
                      <div>
                        <p className={`font-medium ${item.status === 'COMPLETE' ? 'line-through text-[#5A5A5A]' : 'text-[#323033]'}`}>
                          {item.description}
                        </p>
                        {item.status !== 'PENDING' && (
                          <p className="text-sm text-[#5A5A5A] mt-1">
                            {item.status === 'COMPLETE' ? 'Completed' : 'Marked incomplete'} by {item.completed_by_name || 'Unknown'}
                            {item.completed_at && ` on ${new Date(item.completed_at).toLocaleDateString()}`}
                          </p>
                        )}
                        {item.notes && (
                          <p className="text-sm text-[#808080] mt-1 italic">
                            Note: {item.notes}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {/* RULE 1: Complete/Issue buttons shown for PENDING items, disabled unless status is IN_PROCESS */}
                      {item.status === 'PENDING' && !isFinalStatus && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => canCompleteItems && setCompleteItemId(item.id)}
                            disabled={!canCompleteItems}
                            className={canCompleteItems 
                              ? "text-green-600 border-green-200 hover:bg-green-50" 
                              : "text-gray-400 border-gray-200 cursor-not-allowed opacity-50"
                            }
                            data-testid={`complete-item-${item.id}`}
                            title={!canCompleteItems ? "Line item actions only available when status is In Process" : ""}
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            Complete
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => canCompleteItems && setIncompleteItemId(item.id)}
                            disabled={!canCompleteItems}
                            className={canCompleteItems 
                              ? "text-red-600 border-red-200 hover:bg-red-50" 
                              : "text-gray-400 border-gray-200 cursor-not-allowed opacity-50"
                            }
                            data-testid={`incomplete-item-${item.id}`}
                            title={!canCompleteItems ? "Line item actions only available when status is In Process" : ""}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Issue
                          </Button>
                        </>
                      )}
                      {/* RULE 2: Delete button only for Operator+ before Complete */}
                      {canManageLineItems && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setDeleteItemId(item.id)}
                          className="text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Notes */}
      {schedule.notes && (
        <Card>
          <CardHeader>
            <CardTitle>Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-[#5A5A5A] whitespace-pre-wrap">{schedule.notes}</p>
          </CardContent>
        </Card>
      )}

      {/* Status Change Modal - Using the shared StatusChangeModal component */}
      {schedule && (
        <StatusChangeModal
          open={statusModalOpen}
          onOpenChange={setStatusModalOpen}
          entityType="ws"
          currentStatus={schedule.status}
          previousStatus={schedule.previous_status}
          entityId={schedule.id}
          entityLabel={`${sr?.car_mark} ${sr?.car_number} - ${schedule.job_number || sr?.job_number}`}
          userRole={user?.role || 'OPERATOR'}
          onStatusChange={handleStatusChanged}
          api={api}
          lineItemsComplete={lineItemsComplete}
          totalLineItems={totalLineItems}
          completedLineItems={completedLineItems}
          activeBillingIssue={undefined}
          onFlagBillingIssue={async (note) => {
            try {
              await api.post(`/work-schedules/${schedule.id}/billing-issue`, { issue_note: note });
              toast.success('Billing Issue flagged');
              // Refresh schedule so status_history + list flag stay in sync.
              await loadSchedule();
              await fetchAuditHistory();
            } catch (e) {
              const detail = e?.response?.data?.detail || '';
              const msg = detail.includes('already exists')
                ? 'A Billing Issue is already active for this work schedule. Resolve it before flagging another issue.'
                : (detail || 'Failed to flag Billing Issue.');
              toast.error(msg);
            }
          }}
          onResolveBillingIssue={async (note) => {
            try {
              await api.post(`/work-schedules/${schedule.id}/billing-issue/resolve`, { resolution_note: note });
              toast.success('Billing Issue resolved');
              await loadSchedule();
              await fetchAuditHistory();
            } catch (e) {
              toast.error(e?.response?.data?.detail || 'Failed to resolve Billing Issue.');
            }
          }}
          onCompleteIntercept={() => {
            // v1 Railcar Repair Status Notification — status change modal
            // has closed after confirming Complete; open the required
            // snapshot form. Snapshot POST flips the WS status.
            setRrsnOutcome('COMPLETE');
            setRrsnIssueReason('');
            setRrsnFormOpen(true);
          }}
        />
      )}

      {/* Railcar Repair Status Notification — required work-end form */}
      {schedule && rrsnOutcome && (
        <RailcarRepairStatusNotificationForm
          open={rrsnFormOpen}
          onOpenChange={setRrsnFormOpen}
          workScheduleId={schedule.id}
          outcome={rrsnOutcome}
          defaultLoadStatus={(sr?.interior_condition || 'CLEAN').toUpperCase()}
          defaultLoadStatusOther={sr?.interior_condition_other || ''}
          issueReason={rrsnIssueReason}
          onSubmitted={() => {
            setRrsnFormOpen(false);
            setRrsnOutcome(null);
            setRrsnIssueReason('');
            loadSchedule();
            loadRrsnList();
          }}
        />
      )}

      {/* Railcar Repair Status Notification — review + approve */}
      {rrsnActive && (
        <RailcarRepairStatusNotificationReviewModal
          open={rrsnReviewOpen}
          onOpenChange={setRrsnReviewOpen}
          snapshot={rrsnActive}
          canApprove={['ADMIN', 'MANAGER', 'OPERATOR'].includes(user?.role)}
          onApproved={(updated) => {
            setRrsnActive(updated);
            loadRrsnList();
          }}
        />
      )}

      {/* Reschedule Modal (Operator+ only) */}
      {schedule && (
        <RescheduleModal
          open={rescheduleModalOpen}
          onOpenChange={setRescheduleModalOpen}
          workSchedule={schedule}
          onReschedule={handleRescheduleComplete}
          api={api}
        />
      )}

      {/* Add Line Item Modal */}
      <Dialog open={addLineItemOpen} onOpenChange={setAddLineItemOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Line Item</DialogTitle>
            <DialogDescription>
              Add a new task to track for this job
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label>Description *</Label>
            <Input
              value={newLineItemDesc}
              onChange={(e) => setNewLineItemDesc(e.target.value)}
              placeholder="e.g., Inspect brake system"
              data-testid="new-line-item-input"
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddLineItemOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddLineItem}
              disabled={!newLineItemDesc.trim() || addingLineItem}
              className="bg-[#005EF8] hover:bg-[#0047C2] text-white"
            >
              {addingLineItem ? 'Adding...' : 'Add Item'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete Item Modal */}
      <Dialog open={!!completeItemId} onOpenChange={() => setCompleteItemId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark Item Complete</DialogTitle>
            <DialogDescription>
              Confirm this line item has been completed
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label>Notes (Optional)</Label>
            <Textarea
              value={completeNotes}
              onChange={(e) => setCompleteNotes(e.target.value)}
              placeholder="Add any completion notes..."
              rows={2}
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteItemId(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleCompleteItem}
              disabled={completingItem}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="confirm-complete-btn"
            >
              {completingItem ? 'Completing...' : 'Mark Complete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Incomplete Item Modal */}
      <Dialog open={!!incompleteItemId} onOpenChange={() => setIncompleteItemId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark Item as Issue</DialogTitle>
            <DialogDescription>
              Report an issue with this line item
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label>Reason *</Label>
            <Textarea
              value={incompleteReason}
              onChange={(e) => setIncompleteReason(e.target.value)}
              placeholder="Describe the issue..."
              rows={3}
              data-testid="incomplete-reason-input"
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIncompleteItemId(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleIncompleteItem}
              disabled={!incompleteReason.trim() || markingIncomplete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {markingIncomplete ? 'Saving...' : 'Report Issue'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Line Item Confirmation */}
      <AlertDialog open={!!deleteItemId} onOpenChange={() => setDeleteItemId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Line Item?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete this line item.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteLineItem}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Status History Modal */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Status History</DialogTitle>
            <DialogDescription>
              Timeline of all status changes for this work schedule
            </DialogDescription>
          </DialogHeader>
          
          <div className="max-h-[400px] overflow-y-auto py-4">
            {schedule.status_history && schedule.status_history.length > 0 ? (
              <div className="space-y-4">
                {[...schedule.status_history].reverse().map((entry, index) => {
                  const isBillingIssueEvent = entry.event_type === 'billing_issue_flagged' || entry.event_type === 'billing_issue_resolved';
                  const entryStatusConfig = getStatusConfig(entry.to_status);
                  const EntryIcon = isBillingIssueEvent ? Flag : (STATUS_ICONS[entry.to_status] || Circle);
                  const label = isBillingIssueEvent ? (entry.label || (entry.event_type === 'billing_issue_flagged' ? 'Billing Issue Flagged' : 'Billing Issue Resolved')) : entryStatusConfig.label;
                  return (
                    <div key={`item-${index}`} className="flex gap-3" data-testid={isBillingIssueEvent ? `history-${entry.event_type}` : undefined}>
                      <div className="flex flex-col items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isBillingIssueEvent ? 'bg-amber-100 text-amber-700' : entryStatusConfig.color}`}>
                          <EntryIcon className="w-4 h-4" />
                        </div>
                        {index < schedule.status_history.length - 1 && (
                          <div className="w-0.5 h-full bg-gray-200 mt-1" />
                        )}
                      </div>
                      <div className="flex-1 pb-4">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-[#323033]">
                            {label}
                          </span>
                          {!isBillingIssueEvent && (
                            <span className="text-xs text-[#808080]">
                              from {getStatusConfig(entry.from_status).label}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-[#5A5A5A]">
                          By {entry.changed_by_name} • {new Date(entry.changed_at).toLocaleString()}
                        </p>
                        {entry.notes && (
                          <p className="text-sm text-[#808080] mt-1 italic">
                            {entry.notes}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-center text-[#5A5A5A] py-4">No status changes recorded</p>
            )}

            {/* 2026-07-06 — Audit Trail block removed from UI (duplicated the
                Status History timeline above). Backend audit-log recording is
                unchanged. */}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Repair Site read-only modal (2026-06-05). Visible to every role that
          can view the WS detail page, including Technicians. The "Entry
          Requirements" section only renders when the linked Customers &
          Contacts repair site has non-empty content. */}
      <Dialog open={siteModalOpen} onOpenChange={setSiteModalOpen}>
        <DialogContent className="max-w-lg" data-testid="ws-repair-site-modal">
          <DialogHeader>
            <DialogTitle className="text-xl">
              {(schedule.repair_site_contact?.repair_site_name)
                || schedule.service_request?.repair_site
                || schedule.service_request?.site_name
                || 'Repair Site'}
            </DialogTitle>
          </DialogHeader>
          {(() => {
            const site = schedule.repair_site_contact;
            if (!site) {
              return (
                <div className="text-sm text-[#5A5A5A] italic">
                  No matching Repair Site or Dedicated Site contact has been saved in Customers &amp; Contacts yet.
                  Add the site there to populate full address and entry details.
                </div>
              );
            }
            // 2026-08-19 — treat blank/null/empty Repair Site Address Line 2
            // as absent. Legacy records may contain literal "N/A" (typed by
            // users when the field was required); render those as blank too
            // without altering the stored value.
            const _isBlankLine = (v) => {
              const s = (v || '').trim();
              return !s || s.toUpperCase() === 'N/A';
            };
            const _line2 = _isBlankLine(site.address_line_2) ? '' : site.address_line_2;
            const addrLine = [site.address, _line2].filter(Boolean).join(', ');
            const cityLine = [site.city, site.state_province, site.zip_code].filter(Boolean).join(', ');
            return (
              <div className="space-y-3 text-sm">
                {site.splc_code && (
                  <div>
                    <p className="text-xs uppercase tracking-wide text-[#5A5A5A]">SPLC Code</p>
                    <p className="font-medium">{site.splc_code}</p>
                  </div>
                )}
                {(addrLine || cityLine) && (
                  <div className="flex gap-2">
                    <MapPin className="w-4 h-4 text-[#5A5A5A] mt-0.5 shrink-0" />
                    <div>
                      {addrLine && <p>{addrLine}</p>}
                      {cityLine && <p>{cityLine}</p>}
                    </div>
                  </div>
                )}
                {site.site_contact && (
                  <div>
                    <p className="text-xs uppercase tracking-wide text-[#5A5A5A]">Site Contact</p>
                    <p className="font-medium">{site.site_contact}</p>
                  </div>
                )}
                {site.site_contact_email && (
                  <div className="flex gap-2 items-center">
                    <Mail className="w-4 h-4 text-[#5A5A5A]" />
                    <a className="text-[#005EF8] hover:underline" href={`mailto:${site.site_contact_email}`}>
                      {site.site_contact_email}
                    </a>
                  </div>
                )}
                {site.site_contact_number && (
                  <div className="flex gap-2 items-center">
                    <Phone className="w-4 h-4 text-[#5A5A5A]" />
                    <a className="text-[#005EF8] hover:underline" href={`tel:${site.site_contact_number}`}>
                      {site.site_contact_number}
                    </a>
                  </div>
                )}
                {site.entry_requirements && site.entry_requirements.trim() && (
                  <>
                    <hr className="my-2" />
                    <div>
                      <p className="text-xs uppercase tracking-wide text-[#5A5A5A] mb-1">
                        Entry Requirements
                      </p>
                      <p
                        className="whitespace-pre-wrap text-[#323033] text-sm leading-relaxed"
                        data-testid="ws-entry-requirements-body"
                      >
                        {site.entry_requirements}
                      </p>
                    </div>
                  </>
                )}
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSiteModalOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkScheduleDetailPage;
